import { api, apiClient } from './client';
import { uploadDirect } from '@/api/direct-upload.api';

/**
 * Billing API.
 *
 * **Money crosses this boundary as integer minor units (piastres).** Every
 * field named `*Minor` is an integer number of piastres: 59900 is 599.00 EGP.
 * The backend stores money this way because floats lose piastres, and the
 * previous UI's `59,900 EGP/month` bug came from rendering a minor-unit value
 * as though it were EGP.
 *
 * Convert exactly once, at the point of display, with `toMajor()`. Never do
 * arithmetic on the converted value — sum in minor units and convert the total.
 */

/** Piastres → EGP, for handing to <Money />. The only place division happens. */
export const toMajor = (minor: number | null | undefined): number =>
  (minor ?? 0) / 100;

/** EGP → piastres, for values a user typed. Rounds, so 599.005 cannot slip in. */
export const toMinor = (major: number): number => Math.round(major * 100);

export type BillingCycle = 'monthly' | 'yearly';

/**
 * The cycle plans are sold on: yearly only (the API's billing-cycles.ts).
 * `monthly` stays in the type because subscriptions sold before still carry it.
 */
export const SOLD_CYCLE: BillingCycle = 'yearly';

export type SubscriptionStatus =
  | 'active'
  | 'trial'
  | 'grace_period'
  | 'provisional'
  | 'suspended'
  | 'expired'
  | 'canceled';

export type InvoiceStatus =
  | 'draft'
  | 'open'
  | 'awaiting_review'
  | 'paid'
  | 'partially_paid'
  | 'void'
  | 'uncollectible';

export type ProofStatus = 'pending' | 'approved' | 'rejected' | 'superseded';

export type ProofMethod =
  | 'bank_transfer'
  | 'instapay'
  | 'vodafone_cash'
  | 'cash'
  | 'other';

export type RejectionReason =
  | 'illegible'
  | 'amount_mismatch'
  | 'wrong_account'
  | 'duplicate_receipt'
  | 'payment_not_received'
  | 'incomplete_details'
  | 'other';

export interface UsageLine {
  /** `storage_mb` counts in whole MB. */
  key: 'branches' | 'users_per_branch' | 'rooms_per_branch' | 'storage_mb';
  current: number;
  /** null = unlimited. */
  limit: number | null;
  /** 0–1, or null when unlimited. */
  ratio: number | null;
}

export interface BranchUsage {
  branchId: string;
  branchName: string;
  staff: { current: number; limit: number | null };
  rooms: { current: number; limit: number | null };
}

export interface ProofSummary {
  id: number;
  status: ProofStatus;
  rejectionReason: RejectionReason | null;
  rejectionNote: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
}

export interface OpenInvoiceSummary {
  id: number;
  number: string;
  status: InvoiceStatus;
  totalMinor: number;
  paidMinor: number;
  dueMinor: number;
  currency: string;
  issuedAt: string;
  dueAt: string | null;
  latestProof: ProofSummary | null;
  /** Set when paying this invoice applies a plan change or branch add-on. */
  pendingChange: {
    kind: 'plan_change' | 'extra_branches' | 'extra_storage';
    targetPlanId: number | null;
    targetCycle: BillingCycle | null;
    additionalBranches: number | null;
    additionalStorageGb: number | null;
  } | null;
}

export interface SubscriptionOverview {
  hasSubscription: true;
  subscriptionId: number;
  status: SubscriptionStatus;
  plan: {
    id: number;
    code: string;
    name: string;
    billingCycle: BillingCycle;
    /** What this clinic pays per cycle. Null = no price on record. */
    priceMinor: number | null;
    currency: string;
    taxInclusive: boolean;
    taxRateBp: number;
  };
  addOns: {
    additionalBranches: number;
    /** Per extra branch, per month. */
    branchFeeMonthlyMinor: number;
    /** Storage bought on top of the plan, in GB. */
    additionalStorageGb: number;
    /** Per storage pack, per month. 0 = not sold self-serve. */
    storagePackFeeMonthlyMinor: number;
    storagePackGb: number;
  };
  /** When money is next due — distinct from when the term ends. */
  nextPaymentAt: string;
  termEndsAt: string;
  daysUntilNextPayment: number;
  daysUntilTermEnds: number;
  trialEndsAt: string | null;
  graceEndsAt: string;
  daysUntilGraceEnds: number | null;
  provisional: { until: string; proofId: number | null } | null;
  cancelAtPeriodEnd: boolean;
  usage: { lines: UsageLine[]; branchBreakdown: BranchUsage[] };
  openInvoice: OpenInvoiceSummary | null;
}

export type SubscriptionOverviewResponse =
  | SubscriptionOverview
  | { hasSubscription: false };

/**
 * A feature a plan switches on. Mirrors PLAN_FEATURES in the API
 * (billing/entitlements/plan-features.ts) plus the booking app.
 * Listed from the lowest tier up, which is the order cards print them in.
 */
export const PLAN_FEATURE_KEYS = [
  'online_booking',
  'financial_statements',
  'payroll',
  'insurance',
  'inventory',
  'lab_cases',
  'xray_import',
  'auto_recalls',
  'consent_esign',
  'clinic_analytics',
  'loyalty_offers',
  'staff_chat',
  'branch_reports',
  'activity_log',
  'custom_branding',
] as const;
export type PlanFeatureKey = (typeof PLAN_FEATURE_KEYS)[number];

export interface PlanFeatures {
  features: PlanFeatureKey[];
  /** For each feature the plan lacks, the cheapest plan on sale that has it. */
  unlockedBy: Partial<Record<PlanFeatureKey, { planId: number; name: string }>>;
}

export interface PlanCard {
  id: number;
  code: string;
  name: string;
  description: string | null;
  isTrial: boolean;
  prices: {
    monthly: number | null;
    yearly: number | null;
    currency: string;
    taxInclusive: boolean;
    taxRateBp: number;
    yearlySavingMinor: number | null;
    yearlySavingPercent: number | null;
  };
  limits: Record<string, number | null>;
  /** Marketing lines the admin writes. */
  features: string[];
  /** What the plan switches on; this is what the API enforces. */
  planFeatures: PlanFeatureKey[];
}

export interface PaymentInstruction {
  id: number;
  method: ProofMethod;
  displayName: string;
  accountName: string | null;
  accountNumber: string | null;
  bankName: string | null;
  iban: string | null;
  swift: string | null;
  walletNumber: string | null;
  instructionsEn: string | null;
  instructionsAr: string | null;
  sortOrder: number;
}

export interface InvoiceListItem {
  id: number;
  number: string;
  status: InvoiceStatus;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  subtotalMinor: number | null;
  taxMinor: number | null;
  totalMinor: number | null;
  amountPaidMinor: number;
  currency: string;
  hasPdf: boolean;
}

export interface InvoiceLine {
  id: number;
  description: string;
  kind: string;
  quantity: number;
  unitAmountMinor: number | null;
  amountMinor: number | null;
  taxMinor: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  proration: boolean;
}

export interface InvoiceDetail extends InvoiceListItem {
  dueMinor: number;
  taxRateBp: number;
  taxInclusive: boolean;
  billingSnapshot: Record<string, unknown> | null;
  paymentInstructionsSnapshot: {
    methods?: PaymentInstruction[];
    snapshotAt?: string;
  } | null;
  issuerSnapshot: Record<string, unknown> | null;
  etaStatus: string | null;
  etaUuid: string | null;
  items: InvoiceLine[];
}

export interface ProofDetail extends ProofSummary {
  invoiceId: number;
  method: ProofMethod;
  declaredAmountMinor: number;
  approvedAmountMinor: number | null;
  referenceNote: string | null;
  paidAtDeclared: string | null;
  fileMime: string;
}

export interface UploadProofResult {
  id: number;
  status: ProofStatus;
  uploadedAt: string;
  /** Set when a provisional extension was granted; tell the user explicitly. */
  provisionalUntil: string | null;
}

export interface DowngradeBlocker {
  key: string;
  current: number;
  limit: number;
  excess: number;
}

export interface ChangePreview {
  kind: 'plan_change' | 'extra_branches' | 'extra_storage';
  direction: 'upgrade' | 'downgrade' | 'lateral';
  currentPlan: { id: number; name: string; cycle: BillingCycle };
  targetPlan: { id: number; name: string; cycle: BillingCycle } | null;
  remainingDays: number;
  totalPeriodDays: number;
  creditMinor: number;
  newChargeMinor: number;
  amountDueMinor: number;
  taxRateBp: number;
  taxInclusive: boolean;
  currency: string;
  blockers: DowngradeBlocker[];
  canProceed: boolean;
  /** Plan changes: features the clinic has now that the target plan lacks. */
  lostFeatures?: PlanFeatureKey[];
  extraBranches?: {
    count: number;
    perBranchMinor: number;
    feeMonthlyMinor: number;
    currentLimit: number;
    newLimit: number;
  };
  extraStorage?: {
    packs: number;
    packGb: number;
    perPackMinor: number;
    feeMonthlyMinor: number;
    /** Where the storage cap is and where it lands, in MB. */
    currentLimitMb: number;
    newLimitMb: number;
  };
}

export interface ChangeResult {
  invoiceId: number;
  invoiceNumber: string;
  amountDueMinor: number | null;
  preview: ChangePreview;
}

export const billingApi = {
  overview: () =>
    api.get<SubscriptionOverviewResponse>('/billing/subscription'),

  plans: (locale = 'en') =>
    api.get<PlanCard[]>(`/billing/plans?locale=${encodeURIComponent(locale)}`),

  features: (locale = 'en') =>
    api.get<PlanFeatures>(
      `/billing/features?locale=${encodeURIComponent(locale)}`,
    ),

  paymentInstructions: () =>
    api.get<PaymentInstruction[]>('/billing/payment-instructions'),

  invoices: (
    params: {
      status?: string;
      year?: number;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all')
      query.set('status', params.status);
    if (params.year) query.set('year', String(params.year));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return api.get<{
      items: InvoiceListItem[];
      total: number;
      limit: number;
      offset: number;
    }>(`/billing/invoices${qs ? `?${qs}` : ''}`);
  },

  invoice: (id: number) => api.get<InvoiceDetail>(`/billing/invoices/${id}`),

  /** The invoice document, fetched through the authenticated client. */
  invoicePdf: async (id: number): Promise<Blob> => {
    const response = await apiClient.get(`/billing/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  invoiceProofs: (id: number) =>
    api.get<ProofDetail[]>(`/billing/invoices/${id}/proofs`),

  /**
   * Price a plan change. POST despite being read-only: it carries a body and
   * depends on live usage, so it must not be cached the way a GET would be.
   */
  previewChange: (targetPlanId: number, billingCycle: BillingCycle) =>
    api.post<ChangePreview>('/billing/subscription/preview', {
      targetPlanId,
      billingCycle,
    }),

  /** Raises an invoice. The change applies once the operator accepts payment. */
  requestChange: (targetPlanId: number, billingCycle: BillingCycle) =>
    api.post<ChangeResult>('/billing/subscription/change', {
      targetPlanId,
      billingCycle,
    }),

  previewBranches: (additionalBranches: number) =>
    api.post<ChangePreview>('/billing/subscription/branches/preview', {
      additionalBranches,
    }),

  requestBranches: (additionalBranches: number) =>
    api.post<ChangeResult>('/billing/subscription/branches', {
      additionalBranches,
    }),

  previewStorage: (packs: number) =>
    api.post<ChangePreview>('/billing/subscription/storage/preview', {
      packs,
    }),

  requestStorage: (packs: number) =>
    api.post<ChangeResult>('/billing/subscription/storage', { packs }),

  /**
   * Submit a receipt. The file goes straight to storage first; the API gets
   * its key with the rest of the form.
   */
  uploadProof: async (
    invoiceId: number,
    input: {
      file: File;
      method: ProofMethod;
      declaredAmountMinor: number;
      referenceNote?: string;
      paidAtDeclared?: string;
    },
  ) => {
    const uploadKey = await uploadDirect(input.file, 'payment-proof');
    return apiClient
      .post<UploadProofResult>(`/billing/invoices/${invoiceId}/proofs`, {
        uploadKey,
        method: input.method,
        declaredAmountMinor: input.declaredAmountMinor,
        referenceNote: input.referenceNote || undefined,
        paidAtDeclared: input.paidAtDeclared || undefined,
      })
      .then((r) => r.data);
  },
};
